/**
 * useTVMotion
 *
 * Returns Framer Motion prop helpers that are stripped down on TV / Fire Stick.
 *
 * On TV:
 *  - whileHover / whileTap -> empty objects (no pointer device)
 *  - Spring transitions -> short CSS tween (no per-frame JS physics)
 *  - Scale entry animations -> opacity-only (no layout recomputation)
 *
 * On desktop all values pass through unchanged.
 */

import type { TargetAndTransition, Transition } from 'framer-motion';
import { useIsTV } from './useIsTV';

/** Short tween used in place of springs on TV. */
export const TV_TWEEN: Transition = { type: 'tween', duration: 0.18, ease: 'easeOut' };

export function useTVMotion() {
  const isTV = useIsTV();

  return {
    isTV,

    /** whileHover -- no-op on TV. Usage: whileHover={tv.hover({ scale: 1.02 })} */
    hover: (props: TargetAndTransition): TargetAndTransition => (isTV ? {} : props),

    /** whileTap -- no-op on TV. Usage: whileTap={tv.tap({ scale: 0.97 })} */
    tap: (props: TargetAndTransition): TargetAndTransition => (isTV ? {} : props),

    /** Replace spring with tween on TV. Usage: transition={tv.spring({ type: 'spring', ... })} */
    spring: (desktop: Transition): Transition => (isTV ? TV_TWEEN : desktop),

    /**
     * Strip scale from entry animations on TV (opacity-only avoids layout cost).
     * Usage: initial={tv.entry({ opacity: 0, scale: 0.95 })}
     *        animate={tv.entry({ opacity: 1, scale: 1  })}
     */
    entry: (props: TargetAndTransition): TargetAndTransition => {
      if (!isTV) return props;
      const result: TargetAndTransition = {};
      if (props.opacity !== undefined) result.opacity = props.opacity;
      const withY = props as { y?: number };
      if (withY.y !== undefined) result.y = withY.y;
      return result;
    },

    /** Transition for entry animations -- tween on TV. */
    entryTransition: (desktop: Transition): Transition => (isTV ? TV_TWEEN : desktop),
  };
}
